import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Stethoscope, Loader2, Save, Plus, Calendar, User as UserIcon,
  Activity, ClipboardList, Heart, Sparkles, ArrowRight,
} from 'lucide-react';

/**
 * Composant "Carnet de Santé" pour un bâtiment.
 * Affiche : score global, statuts par poste, dernière consultation,
 * possibilité de créer une nouvelle consultation.
 */

const STATUT_COLORS = {
  bon_etat: 'from-green-50 to-emerald-50 border-green-200 text-green-800',
  surveillance: 'from-amber-50 to-yellow-50 border-amber-200 text-amber-800',
  intervention: 'from-red-50 to-rose-50 border-red-200 text-red-800',
  non_evalue: 'from-slate-50 to-slate-100 border-slate-200 text-slate-700',
};

const POSTE_STATUT = (note) => {
  if (note === null || note === undefined) return { label: 'Non évalué', color: 'text-slate-400' };
  if (note >= 8) return { label: 'Bon état', color: 'text-green-700 bg-green-50' };
  if (note >= 6) return { label: 'À surveiller', color: 'text-amber-700 bg-amber-50' };
  if (note >= 4) return { label: 'Dégradé', color: 'text-orange-700 bg-orange-50' };
  return { label: 'Prioritaire', color: 'text-red-700 bg-red-50' };
};

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch (_e) { return iso; }
}

export default function CarnetSantePanel({ site }) {
  const [carnet, setCarnet] = useState(null);
  const [history, setHistory] = useState([]);
  const [refData, setRefData] = useState(null);
  const [pathos, setPathos] = useState([]);
  const [ordonnance, setOrdonnance] = useState(null);
  const [mutuelle, setMutuelle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('vue');
  const [showConsultation, setShowConsultation] = useState(false);
  const [showPathoForm, setShowPathoForm] = useState(false);
  const [editingPatho, setEditingPatho] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [site?.site_id]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h, r, p, o, m] = await Promise.all([
        api.get(`/sites/${site.site_id}/carnet-sante`),
        api.get(`/sites/${site.site_id}/consultations`),
        api.get('/sites/carnet-sante/reference-data'),
        api.get(`/sites/${site.site_id}/pathologies`),
        api.get(`/sites/${site.site_id}/ordonnance`),
        api.get(`/sites/${site.site_id}/mutuelle-batiment`),
      ]);
      setCarnet(c.data); setHistory(h.data.consultations || []); setRefData(r.data);
      setPathos(p.data.pathologies || []); setOrdonnance(o.data); setMutuelle(m.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const generatePdf = async () => {
    setGeneratingPdf(true);
    try {
      const r = await api.post(`/sites/${site.site_id}/carnet-sante/pdf`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `carnet-sante-${(site.nom || 'batiment').toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Carnet de Santé généré');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la génération');
    } finally { setGeneratingPdf(false); }
  };

  if (loading) return (
    <div className="bg-white rounded-lg border border-slate-200 p-8 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
    </div>
  );

  const noConsultation = !carnet?.has_consultation;

  return (
    <div className="space-y-4" data-testid="carnet-sante-panel">
      {/* Tabs */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-1 inline-flex gap-1 overflow-x-auto max-w-full">
          {[
            { id: 'vue', label: "Vue d'ensemble", icon: Activity },
            { id: 'carnet', label: 'Carnet de santé', icon: Heart },
            { id: 'patho', label: 'Pathologies', icon: ClipboardList },
            { id: 'ordonnance', label: 'Ordonnance', icon: ClipboardList },
            { id: 'mutuelle', label: 'Mutuelle & aides', icon: Sparkles },
            { id: 'histo', label: 'Historique', icon: Calendar },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 whitespace-nowrap ${tab === t.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`}
              data-testid={`carnet-tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={generatePdf}
          disabled={generatingPdf || !carnet?.has_consultation}
          data-testid="generate-pdf-btn"
        >
          {generatingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Générer le Carnet de Santé
        </Button>
      </div>

      {/* VUE D'ENSEMBLE */}
      {tab === 'vue' && (
        <div className="space-y-4">
          {noConsultation ? (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center" data-testid="no-consultation">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <Stethoscope className="w-7 h-7 text-slate-500" />
              </div>
              <div className="text-lg font-bold text-slate-800 mb-1">Santé : Non évaluée</div>
              <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                Aucune consultation n'a encore été réalisée pour ce bâtiment.
              </p>
              <Button onClick={() => setShowConsultation(true)} data-testid="first-consultation-btn">
                <Stethoscope className="w-4 h-4 mr-2" />
                Effectuer la première consultation
              </Button>
            </div>
          ) : (
            <>
              <div className={`bg-gradient-to-br ${STATUT_COLORS[carnet.statut.code]} rounded-lg border p-6`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold opacity-70">Score de santé</div>
                    <div className="text-5xl font-bold tabular-nums mt-1">
                      {carnet.score_global !== null ? Math.round(carnet.score_global) : '—'}
                      <span className="text-xl opacity-60 ml-1">/100</span>
                    </div>
                    <div className="text-sm font-semibold mt-1">{carnet.statut.label}</div>
                  </div>
                  <Button onClick={() => setShowConsultation(true)} className="bg-white text-slate-800 hover:bg-slate-50" data-testid="new-consultation-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle consultation
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <IndicatorCard label="Postes évalués" value={carnet.postes.filter(p => p.note !== null).length} total={carnet.postes.length} />
                <IndicatorCard label="À surveiller" value={carnet.postes.filter(p => p.note !== null && p.note >= 6 && p.note < 8).length} accent="amber" />
                <IndicatorCard label="Prioritaires" value={carnet.postes.filter(p => p.note !== null && p.note < 6).length} accent="red" />
                <IndicatorCard label="Bon état" value={carnet.postes.filter(p => p.note !== null && p.note >= 8).length} accent="green" />
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap justify-between gap-3">
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Dernière consultation</div>
                  <div className="text-slate-800 mt-0.5">{fmtDate(carnet.last_consultation?.date_visite)}</div>
                  {carnet.last_consultation?.collaborateur && (
                    <div className="text-xs text-slate-500">par {carnet.last_consultation.collaborateur}</div>
                  )}
                </div>
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Prochaine consultation</div>
                  <div className="text-slate-800 mt-0.5">{fmtDate(carnet.prochaine_consultation) || '—'}</div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* CARNET DE SANTÉ */}
      {tab === 'carnet' && (
        <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
          {carnet.postes.map((p) => {
            const st = POSTE_STATUT(p.note);
            const ev = p.evolution;
            return (
              <div key={p.id} className="p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800">{p.label}</div>
                  {p.commentaire && <div className="text-xs text-slate-500 mt-0.5 truncate">{p.commentaire}</div>}
                </div>
                {ev && (
                  <div className="text-xs text-slate-500 whitespace-nowrap">
                    {ev.from} → <b className={ev.trend === 'improving' ? 'text-green-700' : ev.trend === 'degrading' ? 'text-red-700' : 'text-slate-700'}>{ev.to}</b>
                  </div>
                )}
                <div className={`text-xs px-2 py-1 rounded-full font-semibold whitespace-nowrap ${st.color}`}>
                  {p.note !== null ? `${p.note}/10 · ${st.label}` : 'Non évalué'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'patho' && (
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-900">Pathologies identifiées</h3>
            <Button size="sm" onClick={() => { setEditingPatho(null); setShowPathoForm(true); }} data-testid="add-patho-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
          {pathos.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Aucune pathologie enregistrée</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {pathos.map((p) => {
                const meta = p.priorite_meta || {};
                const colors = { red: 'bg-red-100 text-red-800', amber: 'bg-amber-100 text-amber-800', green: 'bg-green-100 text-green-800' };
                return (
                  <button key={p.pathologie_id} onClick={() => { setEditingPatho(p); setShowPathoForm(true); }}
                    className="w-full text-left p-4 hover:bg-slate-50 flex items-start gap-3" data-testid={`patho-${p.pathologie_id}`}>
                    <span className={`flex-shrink-0 mt-0.5 w-2.5 h-2.5 rounded-full ${p.priorite === 'rouge' ? 'bg-red-500' : p.priorite === 'orange' ? 'bg-amber-500' : 'bg-green-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                        {p.titre}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${colors[meta.color] || 'bg-slate-100 text-slate-700'}`}>{meta.label}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{p.statut_label}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {p.poste_label}{p.localisation ? ` · ${p.localisation}` : ''}
                        {p.budget_ht ? ` · ${Math.round(p.budget_ht).toLocaleString('fr-FR')} € HT` : ''}
                        {p.mutuelle_dispositif ? ` · 🩺 ${p.mutuelle_dispositif}` : ''}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'ordonnance' && ordonnance && (
        <div className="space-y-3">
          {ordonnance.synthese.budget_brut > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-200 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Budget brut</div><div className="text-xl font-bold text-slate-900 tabular-nums">{Math.round(ordonnance.synthese.budget_brut).toLocaleString('fr-FR')} €</div></div>
              <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Aides à vérifier</div><div className="text-xl font-bold text-amber-700 tabular-nums">{Math.round(ordonnance.synthese.aides_a_verifier).toLocaleString('fr-FR')} €</div></div>
              <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Aides validées</div><div className="text-xl font-bold text-green-700 tabular-nums">{Math.round(ordonnance.synthese.aides_validees).toLocaleString('fr-FR')} €</div></div>
              <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Reste prévisionnel</div><div className="text-xl font-bold text-blue-700 tabular-nums">{ordonnance.synthese.reste_previsionnel !== null ? `${Math.round(ordonnance.synthese.reste_previsionnel).toLocaleString('fr-FR')} €` : '—'}</div></div>
            </div>
          )}
          {ordonnance.groups.map((g) => (
            <div key={g.key} className="bg-white rounded-lg border border-slate-200">
              <div className={`p-3 border-b border-slate-100 flex items-center justify-between ${g.key === 'rouge' ? 'bg-red-50' : g.key === 'orange' ? 'bg-amber-50' : 'bg-green-50'}`}>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-700">{g.label}</div>
                  <div className="text-[11px] text-slate-500">{g.delai}</div>
                </div>
                {g.total_budget > 0 && <div className="text-lg font-bold text-slate-900 tabular-nums">{Math.round(g.total_budget).toLocaleString('fr-FR')} €</div>}
              </div>
              {g.items.length === 0 ? (
                <div className="p-4 text-xs text-slate-400 text-center">Aucune pathologie</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {g.items.map((p) => (
                    <div key={p.pathologie_id} className="p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0"><div className="font-semibold text-sm text-slate-900 truncate">{p.titre}</div>
                        <div className="text-xs text-slate-500 truncate">{p.localisation || '—'}</div></div>
                      <div className="text-sm font-bold text-slate-900 tabular-nums whitespace-nowrap">
                        {p.budget_ht ? `${Math.round(p.budget_ht).toLocaleString('fr-FR')} €` : 'à chiffrer'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'mutuelle' && mutuelle && (
        <div className="space-y-3">
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider font-bold text-blue-900">🩺 Mutuelle du Bâtiment</div>
            <div className="text-sm text-slate-600 mt-0.5">CEE & dispositifs de financement</div>
          </div>
          {mutuelle.pathologies.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-sm text-slate-500">
              Aucun dispositif détecté sur les pathologies actuelles.
            </div>
          ) : mutuelle.pathologies.map((p) => {
            const etatColor = p.mutuelle_statut === 'validee' ? 'bg-green-100 text-green-800'
              : p.mutuelle_statut === 'a_verifier' ? 'bg-amber-100 text-amber-800'
              : 'bg-blue-100 text-blue-800';
            return (
              <div key={p.pathologie_id} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-slate-900">{p.titre}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.mutuelle_dispositif}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${etatColor}`}>{p.mutuelle_statut_label}</span>
                </div>
                {p.mutuelle_aide_estimee && (
                  <div className="mt-2 text-sm">Aide estimative : <b>{Math.round(p.mutuelle_aide_estimee).toLocaleString('fr-FR')} €</b></div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {tab === 'histo' && (
        <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
          {history.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Aucune consultation enregistrée</div>
          ) : history.map((c, i) => (
            <div key={c.consultation_id} className="p-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 font-bold text-sm">
                {history.length - i}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800">{fmtDate(c.date_visite)}</div>
                <div className="text-xs text-slate-500">
                  {c.collaborateur && `par ${c.collaborateur} · `}
                  Score {c.score_global !== null ? `${Math.round(c.score_global)}/100` : 'non calculé'}
                </div>
                {c.commentaire && <div className="text-sm text-slate-600 mt-1">{c.commentaire}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showConsultation && refData && (
        <ConsultationModal
          site={site}
          postes={refData.postes}
          onClose={() => setShowConsultation(false)}
          onSaved={() => { setShowConsultation(false); load(); }}
        />
      )}
      {showPathoForm && refData && (
        <PathologieForm
          site={site}
          postes={refData.postes}
          existing={editingPatho}
          onClose={() => { setShowPathoForm(false); setEditingPatho(null); }}
          onSaved={() => { setShowPathoForm(false); setEditingPatho(null); load(); }}
        />
      )}
    </div>
  );
}

function PathologieForm({ site, postes, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    poste_id: existing?.poste_id || postes[0]?.id || '',
    titre: existing?.titre || '',
    localisation: existing?.localisation || '',
    symptome: existing?.symptome || '',
    prescription: existing?.prescription || '',
    priorite: existing?.priorite || 'orange',
    statut: existing?.statut || 'identifiee',
    budget_ht: existing?.budget_ht || '',
    mutuelle_dispositif: existing?.mutuelle_dispositif || '',
    mutuelle_aide_estimee: existing?.mutuelle_aide_estimee || '',
    mutuelle_statut: existing?.mutuelle_statut || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.titre.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget_ht: form.budget_ht ? parseFloat(String(form.budget_ht).replace(',', '.')) : null,
        mutuelle_aide_estimee: form.mutuelle_aide_estimee ? parseFloat(String(form.mutuelle_aide_estimee).replace(',', '.')) : null,
        mutuelle_dispositif: form.mutuelle_dispositif.trim() || null,
        mutuelle_statut: form.mutuelle_statut || null,
      };
      if (existing) await api.patch(`/sites/${site.site_id}/pathologies/${existing.pathologie_id}`, payload);
      else await api.post(`/sites/${site.site_id}/pathologies`, payload);
      toast.success(existing ? 'Pathologie modifiée' : 'Pathologie créée');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!existing || !window.confirm('Supprimer cette pathologie ?')) return;
    setSaving(true);
    try {
      await api.delete(`/sites/${site.site_id}/pathologies/${existing.pathologie_id}`);
      toast.success('Supprimée'); onSaved();
    } catch (e) { toast.error('Échec'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="patho-modal">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">{existing ? 'Modifier la pathologie' : 'Nouvelle pathologie'}</h2>
        </div>
        <div className="p-6 space-y-4">
          <Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Titre (ex : Parquet endommagé)" data-testid="patho-titre" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.poste_id} onChange={(e) => setForm({ ...form, poste_id: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="patho-poste">
              {postes.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <select value={form.priorite} onChange={(e) => setForm({ ...form, priorite: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="patho-priorite">
              <option value="rouge">🔴 Prioritaire (0-6 mois)</option>
              <option value="orange">🟠 À anticiper (6-24 mois)</option>
              <option value="vert">🟢 Surveillance (2-5 ans)</option>
            </select>
          </div>
          <Input value={form.localisation} onChange={(e) => setForm({ ...form, localisation: e.target.value })} placeholder="Localisation (ex : Couloir 1er étage)" />
          <Textarea rows={2} value={form.symptome} onChange={(e) => setForm({ ...form, symptome: e.target.value })} placeholder="Constat / symptôme observé" />
          <Textarea rows={2} value={form.prescription} onChange={(e) => setForm({ ...form, prescription: e.target.value })} placeholder="Prescription / travaux recommandés" />
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" value={form.budget_ht} onChange={(e) => setForm({ ...form, budget_ht: e.target.value })} placeholder="Budget HT (€)" data-testid="patho-budget" />
            <select value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-sm">
              <option value="identifiee">Identifiée</option>
              <option value="a_chiffrer">À chiffrer</option>
              <option value="planifiee">Planifiée</option>
              <option value="en_travaux">En travaux</option>
              <option value="traitee">Traitée</option>
              <option value="surveillance">Surveillance</option>
            </select>
          </div>
          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">🩺 Mutuelle du Bâtiment (optionnel)</div>
            <div className="grid grid-cols-2 gap-3">
              <Input value={form.mutuelle_dispositif} onChange={(e) => setForm({ ...form, mutuelle_dispositif: e.target.value })} placeholder="Dispositif (ex : CEE)" />
              <Input type="number" value={form.mutuelle_aide_estimee} onChange={(e) => setForm({ ...form, mutuelle_aide_estimee: e.target.value })} placeholder="Aide estimée (€)" />
            </div>
            <select value={form.mutuelle_statut} onChange={(e) => setForm({ ...form, mutuelle_statut: e.target.value })} className="mt-2 border border-slate-300 rounded-md px-3 py-2 text-sm w-full">
              <option value="">— État aide —</option>
              <option value="potentielle">Éligibilité potentielle</option>
              <option value="a_verifier">À vérifier</option>
              <option value="validee">Prise en charge validée</option>
            </select>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-between gap-2 sticky bottom-0 bg-white">
          {existing ? <Button variant="outline" onClick={remove} disabled={saving} className="text-red-600 border-red-200">Supprimer</Button> : <div />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="patho-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IndicatorCard({ label, value, total, accent }) {
  const colors = {
    green: 'bg-green-50 border-green-200 text-green-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  };
  const cls = colors[accent] || 'bg-white border-slate-200 text-slate-800';
  return (
    <div className={`rounded-lg border ${cls} p-3 text-center`}>
      <div className="text-2xl font-bold tabular-nums">
        {value}{total !== undefined && <span className="text-sm font-normal opacity-60"> / {total}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold mt-0.5">{label}</div>
    </div>
  );
}

function ConsultationModal({ site, postes, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date_visite: today,
    collaborateur: '',
    commentaire: '',
    prochaine_consultation: '',
  });
  const [notes, setNotes] = useState({}); // poste_id => {note, etat, commentaire}
  const [saving, setSaving] = useState(false);

  const setNote = (id, patch) => setNotes((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        date_visite: form.date_visite,
        collaborateur: form.collaborateur.trim(),
        commentaire: form.commentaire.trim(),
        prochaine_consultation: form.prochaine_consultation || null,
        postes: postes.map((p) => {
          const n = notes[p.id] || {};
          return {
            id: p.id,
            note: n.note !== undefined && n.note !== '' ? parseFloat(n.note) : null,
            etat: n.etat || null,
            commentaire: n.commentaire || '',
          };
        }),
      };
      await api.post(`/sites/${site.site_id}/consultations`, payload);
      toast.success('Consultation enregistrée');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="consultation-modal">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-blue-600" />
            Nouvelle consultation
          </h2>
          <p className="text-sm text-slate-500 mt-1">{site.nom}</p>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Date de visite</label>
              <Input type="date" value={form.date_visite} onChange={(e) => setForm({ ...form, date_visite: e.target.value })} className="mt-1" data-testid="consult-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Collaborateur</label>
              <Input value={form.collaborateur} onChange={(e) => setForm({ ...form, collaborateur: e.target.value })} placeholder="Ex : Jean Dupont" className="mt-1" data-testid="consult-collab" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Commentaire général</label>
            <Textarea rows={2} value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} placeholder="Résumé de la visite" className="mt-1" data-testid="consult-comment" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Prochaine consultation recommandée</label>
            <Input type="date" value={form.prochaine_consultation} onChange={(e) => setForm({ ...form, prochaine_consultation: e.target.value })} className="mt-1" data-testid="consult-next" />
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm font-semibold text-slate-800 mb-2">Évaluation des postes</div>
            <p className="text-xs text-slate-500 mb-3">Note sur 10 (laissez vide si non évalué).</p>
            <div className="space-y-2">
              {postes.map((p) => {
                const n = notes[p.id]?.note;
                const st = POSTE_STATUT(n === '' ? null : parseFloat(n));
                return (
                  <div key={p.id} className="grid grid-cols-[1fr_100px_auto] gap-2 items-center" data-testid={`consult-poste-${p.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 text-sm truncate">{p.label}</div>
                    </div>
                    <Input
                      type="number"
                      min="0" max="10" step="0.5"
                      placeholder="—"
                      value={notes[p.id]?.note ?? ''}
                      onChange={(e) => setNote(p.id, { note: e.target.value })}
                      className="text-center"
                      data-testid={`consult-note-${p.id}`}
                    />
                    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold whitespace-nowrap ${st.color}`}>
                      {st.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="consult-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer la consultation
          </Button>
        </div>
      </div>
    </div>
  );
}
